"use client";

import React, { useState } from "react";
import Image from "next/image";
import { getStrapiMedia } from "@/utils/api-helpers";
import ArrowIcon from "@/app/assets/arrow.svg";

type ContentItem = {
    heading?: string | null;
    description: string;
};

type ChallengeType = {
    id: number;
    heading: string;
    description: string;
    media?: {
        url: string;
        alternativeText?: string | null;
    };
    contents: ContentItem[];
};

type SolutionType = {
    id: number;
    heading: string;
    description: string;
    contents: {
        heading: string;
        description: string;
    }[];
};

type ResultsType = {
    id: number;
    heading: string;
    description: string;
    bottomText?: string;
    media?: {
        url: string;
        alternativeText?: string | null;
    };
};

type CSRNavCardsProps = {
    challenge: ChallengeType;
    solution: SolutionType;
    results: ResultsType;
};

export default function CSRNavCards({
    challenge,
    solution,
    results,
}: CSRNavCardsProps) {
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const toggleSection = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="bg-white rounded-2xl md:rounded-3xl overflow-hidden shadow-sm">
                <button
                    onClick={() => toggleSection(0)}
                    className="w-full px-6 py-5 md:px-10 md:py-6 flex items-center justify-between cursor-pointer group"
                >
                    <h3 className="text-purple font-bold text-lg md:text-xl lg:text-2xl uppercase tracking-tight">
                        {challenge.heading}
                    </h3>
                    <div
                        className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center transition-transform duration-300 ${
                            openIndex === 0 ? "rotate-[135deg]" : ""
                        }`}
                    >
                        <Image
                            src={ArrowIcon}
                            alt="Toggle"
                            className="w-6 h-6 md:w-8 md:h-8"
                        />
                    </div>
                </button>

                <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                        openIndex === 0
                            ? "max-h-[2000px] opacity-100"
                            : "max-h-0 opacity-0"
                    }`}
                >
                    <div className="px-6 pb-8 md:px-10 md:pb-10">
                        <div
                            className="text-gray-700 text-base md:text-lg leading-relaxed mb-8"
                            dangerouslySetInnerHTML={{
                                __html: challenge.description,
                            }}
                        />

                        <div className="flex flex-col lg:flex-row gap-8">
                            {challenge.media?.url && (
                                <div className="w-full lg:w-[40%] flex justify-center items-center">
                                    <Image
                                        src={getStrapiMedia(
                                            challenge.media.url
                                        )}
                                        width={400}
                                        height={400}
                                        alt={
                                            challenge.media.alternativeText ||
                                            "Challenge"
                                        }
                                        className="w-auto h-auto max-h-[350px] object-contain"
                                    />
                                </div>
                            )}

                            <div className="w-full lg:w-[60%] flex flex-col">
                                {challenge.contents?.map((item, index) => (
                                    <div
                                        key={index}
                                        className="py-4 border-b border-gray-200 last:border-b-0 first:pt-0"
                                    >
                                        <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                                            {item.description}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div
                className={`${
                    openIndex === 1 ? "bg-white" : "bg-[#E8F9F8]"
                } rounded-2xl md:rounded-3xl overflow-hidden transition-all duration-500 shadow-sm`}
            >
                <button
                    onClick={() => toggleSection(1)}
                    className="w-full px-6 py-5 md:px-10 md:py-6 flex items-center justify-between cursor-pointer group"
                >
                    <h3 className="text-purple font-bold text-lg md:text-xl lg:text-2xl uppercase tracking-tight">
                        {solution.heading}
                    </h3>
                    <div
                        className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center transition-transform duration-300 ${
                            openIndex === 1 ? "rotate-[135deg]" : ""
                        }`}
                    >
                        <Image
                            src={ArrowIcon}
                            alt="Toggle"
                            className="w-6 h-6 md:w-8 md:h-8"
                        />
                    </div>
                </button>

                <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                        openIndex === 1
                            ? "max-h-[3000px] opacity-100"
                            : "max-h-0 opacity-0"
                    }`}
                >
                    <div className="px-6 pb-8 md:px-10 md:pb-10">
                        <div
                            className="text-gray-700 text-base md:text-lg leading-relaxed mb-8"
                            dangerouslySetInnerHTML={{
                                __html: solution.description,
                            }}
                        />

                        <div className="flex flex-col gap-4">
                            {solution.contents?.map((item, index) => (
                                <div
                                    key={index}
                                    className="bg-gradient-to-r from-[#37DED7] to-[#5CE8E3] rounded-xl md:rounded-2xl p-5 md:p-6 flex flex-col md:flex-row gap-4 md:gap-6 items-start"
                                >
                                    <span className="text-gray-800 font-bold text-3xl md:text-4xl lg:text-5xl leading-none min-w-[60px]">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>

                                    <div className="flex flex-col md:flex-row md:gap-8 flex-1">
                                        <h4 className="text-gray-900 font-bold text-base md:text-lg lg:text-xl mb-2 md:mb-0 md:min-w-[200px] lg:min-w-[250px]">
                                            {item.heading}
                                        </h4>
                                        <p className="text-gray-800 text-sm md:text-base leading-relaxed flex-1">
                                            {item.description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div
                className={`${
                    openIndex === 2 ? "bg-white" : "bg-[#ACEFEC]"
                } rounded-2xl md:rounded-3xl overflow-hidden transition-all duration-500 shadow-sm`}
            >
                <button
                    onClick={() => toggleSection(2)}
                    className="w-full px-6 py-5 md:px-10 md:py-6 flex items-center justify-between cursor-pointer group"
                >
                    <h3 className="text-purple font-bold text-lg md:text-xl lg:text-2xl uppercase tracking-tight">
                        {results.heading}
                    </h3>
                    <div
                        className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center transition-transform duration-300 ${
                            openIndex === 2 ? "rotate-[135deg]" : ""
                        }`}
                    >
                        <Image
                            src={ArrowIcon}
                            alt="Toggle"
                            className="w-6 h-6 md:w-8 md:h-8"
                        />
                    </div>
                </button>

                <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                        openIndex === 2
                            ? "max-h-[2000px] opacity-100"
                            : "max-h-0 opacity-0"
                    }`}
                >
                    <div className="px-6 pb-8 md:px-10 md:pb-10">
                        <div className="flex flex-col lg:flex-row gap-8">
                            <div className="w-full lg:w-[55%]">
                                <div
                                    className="text-gray-700 text-base md:text-lg leading-relaxed"
                                    dangerouslySetInnerHTML={{
                                        __html: results.description,
                                    }}
                                />
                            </div>

                            {results.media?.url && (
                                <div className="w-full lg:w-[45%] flex justify-center items-center">
                                    <Image
                                        src={getStrapiMedia(results.media.url)}
                                        width={450}
                                        height={350}
                                        alt={
                                            results.media.alternativeText ||
                                            "Results"
                                        }
                                        className="w-auto h-auto max-h-[300px] object-contain"
                                    />
                                </div>
                            )}
                        </div>

                        {results.bottomText && (
                            <div className="mt-8 bg-[#E8E0F8]/80 rounded-xl md:rounded-2xl px-6 py-5 md:px-12 md:py-6 border border-purple/10">
                                <p className=" text-sm md:text-base lg:text-xl leading-relaxed font-semibold text-center">
                                    {results.bottomText}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
